import "./mathJaxGlobals";

export const MATHJAX_CDN_URL =
  "https://cdn.jsdelivr.net/npm/mathjax@4/tex-mml-chtml.js";

const SCRIPT_ATTR = "data-selflearn-mathjax";

let loadPromise: Promise<void> | null = null;

function applyPreConfig(): void {
  if (typeof window === "undefined") return;
  const existing = window.MathJax;
  if (existing?.version) return;

  window.MathJax = {
    ...(window.MathJax ?? {}),
    startup: {
      ...((window.MathJax?.startup as object) ?? {}),
      typeset: false,
    },
    output: {
      ...((window.MathJax?.output as object) ?? {}),
      mtextInheritFont: true,
    },
    tex: {
      ...((window.MathJax?.tex as object) ?? {}),
      inlineMath: [
        ["\\(", "\\)"],
        ["$", "$"],
      ],
      displayMath: [
        ["\\[", "\\]"],
        ["$$", "$$"],
      ],
    },
    chtml: {
      ...((window.MathJax?.chtml as object) ?? {}),
      matchFontHeight: true,
    },
    options: {
      ...((window.MathJax?.options as object) ?? {}),
      enableMenu: false,
      renderActions: {
        ...((
          window.MathJax?.options as { renderActions?: Record<string, unknown> }
        )?.renderActions ?? {}),
        explorable: [],
      },
      menuOptions: {
        ...((
          window.MathJax?.options as { menuOptions?: Record<string, unknown> }
        )?.menuOptions ?? {}),
        settings: {
          ...((
            window.MathJax?.options as {
              menuOptions?: { settings?: Record<string, unknown> };
            }
          )?.menuOptions?.settings ?? {}),
          speech: false,
          braille: false,
        },
      },
      a11y: {
        ...((window.MathJax?.options as { a11y?: Record<string, unknown> })
          ?.a11y ?? {}),
        backgroundOpacity: 0,
      },
    },
  };
}

function waitForMathJaxReady(): Promise<void> {
  const mj = window.MathJax;
  const p = mj?.startup?.promise;
  if (p) {
    return p.then(() => undefined);
  }
  if (mj?.typesetPromise) {
    return Promise.resolve();
  }
  return Promise.resolve();
}

export function ensureMathJaxLoaded(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }

  if (window.MathJax?.typesetPromise) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    applyPreConfig();

    const existing = document.querySelector<HTMLScriptElement>(
      `script[${SCRIPT_ATTR}="1"]`,
    );

    const finish = () => {
      waitForMathJaxReady().then(resolve).catch(reject);
    };

    if (existing) {
      if (window.MathJax?.typesetPromise) {
        resolve();
        return;
      }
      const onLoad = () => finish();
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", () => {
        reject(new Error("MathJax script failed"));
      });
      void Promise.resolve().then(() => {
        if (window.MathJax?.typesetPromise) {
          existing.removeEventListener("load", onLoad);
          finish();
        }
      });
      return;
    }

    const script = document.createElement("script");
    script.src = MATHJAX_CDN_URL;
    script.async = true;
    script.setAttribute(SCRIPT_ATTR, "1");
    script.onload = () => finish();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("MathJax load failed"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
